import { useMemo } from 'react';

import type { CreateChartPayload } from '@/entities/chart/api/types';
import {
  CHART_FIELD,
  CHART_FIELD_ITEM_SELECTION_METHOD,
  CHART_GRAPH,
} from '@/entities/chart/model/constants';
import useChartLabel from '@/features/(charts)/chart-renderer/hooks/use-chart-label';
import chartUtils from '@/features/(charts)/chart-renderer/lib/chart-utils';
import type { WordCloudItem } from '@/features/WordCloud/types';

import { buildWordCloudItemsFromTable } from './word-cloud-items';

type Props = {
  chart: ChartBase | CreateChartPayload;
  tableData?: ChartTableRes;
};

const useChartWordCloudItems = ({
  chart,
  tableData,
}: Props): WordCloudItem[] => {
  const getLabel = useChartLabel(tableData?.field_item_meta);

  return useMemo(() => {
    const sortableChart: ChartBase = {
      ...chart,
      graph: chart.graph ?? CHART_GRAPH.WORD_CLOUD,
      historical: chart.historical ?? {
        value: false,
        frequency: null,
      },
      field: chart.field ?? {
        type: CHART_FIELD.FEEDBACK_ALL,
        entire: true,
        candidates: [],
      },
      selected_field: chart.selected_field ?? {
        method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_50,
        items: [],
      },
      series: chart.series ?? [],
    };

    const sortedTableData = tableData
      ? chartUtils.getSortedRowItems(
          sortableChart,
          tableData,
          sortableChart.selected_field.items || [],
        )
      : tableData;

    return buildWordCloudItemsFromTable({
      tableData: sortedTableData,
      getLabel: (alias) =>
        getLabel(chart.field?.type || CHART_FIELD.FEEDBACK_ALL, alias),
    });
  }, [chart, getLabel, tableData]);
};

export default useChartWordCloudItems;
